/**
 * A2 — cliente HTTP do endpoint A1 (extração por imagem).
 *
 * `expenseFromImage(file, note)` envia multipart pra
 * `POST /api/expense/from-image` e devolve o JSON:
 *   - sucesso: `{ ok: true, extracted: {...}, trace_id }`
 *   - erro:    `{ ok: false, error_code, error, trace_id }`
 *
 * Erros de rede viram `{ ok: false, error_code: "NETWORK", error }`
 * — para a UI tratar uniformemente.
 *
 * Usa o helper `authFetch` (injeção automática de JWT).
 */
import { authFetch } from "@/lib/authFetch";
import { compressImage } from "@/utils/image";

const API_URL = process.env.REACT_APP_BACKEND_URL || "";


/**
 * Faz upload e extração.
 *
 * @param {File|Blob} file  arquivo selecionado/capturado.
 * @param {string=}   note  observação opcional do usuário.
 * @returns {Promise<{ok: boolean, extracted?: object, error_code?: string,
 *                    error?: string, trace_id?: string}>}
 */
export async function expenseFromImage(file, note) {
  if (!file) {
    return {
      ok: false,
      error_code: "IMAGE_UNREADABLE",
      error: "Arquivo vazio.",
    };
  }

  // Compressão client-side reduz banda + custo de extração na A1.
  let blob;
  try {
    blob = await compressImage(file, 1024, 0.85);
  } catch (e) {
    return {
      ok: false,
      error_code: "IMAGE_UNREADABLE",
      error: `Falha ao processar a imagem localmente: ${e?.message || e}`,
    };
  }

  // Filename preservado (backend ignora, mas ajuda no log do server).
  const filename = (file.name || "receipt.jpg").replace(/\.heic$/i, ".heic");
  const form = new FormData();
  form.append("image", blob, filename);
  if (note && note.trim()) {
    form.append("note", note.trim());
  }

  try {
    const resp = await authFetch(`${API_URL}/api/expense/from-image`, {
      method: "POST",
      body: form,
    });
    // O endpoint A1 sempre devolve JSON, mesmo em erro de negócio
    // (segue o padrão Cluster B: 200 + ok:false). Status != 2xx só
    // em auth falhada (401/403) ou erro infra (5xx).
    let body;
    try {
      body = await resp.json();
    } catch {
      body = null;
    }
    if (!resp.ok) {
      return {
        ok: false,
        error_code: body?.error_code || (resp.status === 401 || resp.status === 403
          ? "AUTH"
          : "OPENAI_API_ERROR"),
        error: body?.error || `HTTP ${resp.status}`,
        trace_id: body?.trace_id,
      };
    }
    return body || { ok: false, error_code: "EXTRACTION_FAILED", error: "resposta vazia" };
  } catch (e) {
    return {
      ok: false,
      error_code: "NETWORK",
      error: `Falha de rede: ${e?.message || e}`,
    };
  }
}
